'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import { getSettings } from '@/lib/storage';
import { applyTheme, applyAccentColor } from '@/lib/theme';

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  // Apply saved settings on mount
  useEffect(() => {
    const settings = getSettings();
    applyTheme(settings.theme);
    applyAccentColor(settings.accentColor);
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;

    if (e.key === 'd' && !e.metaKey && !e.ctrlKey) {
      router.push('/dashboard');
    } else if (e.key === 'r' && !e.metaKey && !e.ctrlKey) {
      router.push('/roadmap-view');
    } else if (e.key === 't' && !e.metaKey && !e.ctrlKey) {
      router.push('/week-detail-view');
    }
  }, [router]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      style={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--bg-page)', color: 'var(--text-primary)' }}
    >
      {/* Desktop Sidebar */}
      <div
        className="hidden lg:block flex-shrink-0 transition-all duration-300"
        style={{
          width: sidebarCollapsed ? '60px' : '220px',
          transition: 'width 300ms ease',
        }}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
          currentPath={pathname}
        />
      </div>

      {/* Main content */}
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{ paddingBottom: '80px' }}
      >
        <main className="flex-1">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="lg:hidden">
        <MobileNav currentPath={pathname} />
      </div>
    </div>
  );
}